/**
 * Performance testing utilities for image loading optimization
 */

/**
 * Test background image loading performance
 */
export const testBackgroundLoading = () => {
  const startTime = performance.now();
  
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.name.includes('mountain_sunset_background.webp')) {
        const loadTime = performance.now() - startTime;
        console.log(`🖼️ Background image loaded in ${loadTime.toFixed(2)}ms`);
        
        // Log to analytics if available
        if (window.gtag) {
          window.gtag('event', 'background_image_load', {
            event_category: 'performance',
            event_label: 'background_load_time',
            value: Math.round(loadTime)
          });
        }
      }
    }
  });
  
  observer.observe({ entryTypes: ['resource'] });
  
  // Clean up after 30 seconds
  setTimeout(() => observer.disconnect(), 30000);
};

/**
 * Monitor Core Web Vitals related to images
 */
export const monitorImagePerformance = () => {
  // Monitor LCP (Largest Contentful Paint)
  new PerformanceObserver((entryList) => {
    for (const entry of entryList.getEntries()) {
      if (entry.element && entry.element.tagName === 'IMG') {
        console.log(`📊 LCP Image: ${entry.startTime.toFixed(2)}ms`, entry.element.src);
      }
    }
  }).observe({ entryTypes: ['largest-contentful-paint'] });

  // Monitor layout shifts (CLS)
  new PerformanceObserver((entryList) => {
    for (const entry of entryList.getEntries()) {
      if (entry.value > 0.1) { // Significant layout shift
        console.warn(`⚠️ Layout shift detected: ${entry.value.toFixed(4)}`);
      }
    }
  }).observe({ entryTypes: ['layout-shift'] });
};

/**
 * Test lazy loading effectiveness
 */
export const testLazyLoading = () => {
  let lazyImagesLoaded = 0;
  let totalLazyImages = 0;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && entry.target.tagName === 'IMG') {
        const img = entry.target;
        if (img.loading === 'lazy' || img.classList.contains('optimized-image')) {
          lazyImagesLoaded++;
          console.log(`🔄 Lazy image loaded: ${lazyImagesLoaded}/${totalLazyImages}`);
        }
      }
    });
  });

  // Count and observe lazy images
  setTimeout(() => {
    const lazyImages = document.querySelectorAll('img[loading="lazy"], .optimized-image');
    totalLazyImages = lazyImages.length;
    lazyImages.forEach(img => observer.observe(img));
    
    console.log(`📋 Monitoring ${totalLazyImages} lazy images`);
  }, 1000);
};

/**
 * Compare before/after loading times
 */
export const compareLoadingTimes = () => {
  const measurements = {
    backgroundStart: null,
    backgroundEnd: null,
    firstImageStart: null,
    firstImageEnd: null,
    totalImages: 0,
    loadedImages: 0
  };

  // Measure background loading
  const bgImg = document.querySelector('.bg-img');
  if (bgImg) {
    measurements.backgroundStart = performance.now();
    bgImg.addEventListener('load', () => {
      measurements.backgroundEnd = performance.now();
      const bgTime = measurements.backgroundEnd - measurements.backgroundStart;
      console.log(`🌄 Background loaded in ${bgTime.toFixed(2)}ms`);
    });
  }

  // Measure first content image
  const firstContentImg = document.querySelector('.post-card__cover-img');
  if (firstContentImg) {
    measurements.firstImageStart = performance.now();
    firstContentImg.addEventListener('load', () => {
      measurements.firstImageEnd = performance.now();
      const firstImgTime = measurements.firstImageEnd - measurements.firstImageStart;
      console.log(`🎯 First content image loaded in ${firstImgTime.toFixed(2)}ms`);
    });
  }

  // Count all images
  const allImages = document.querySelectorAll('img');
  measurements.totalImages = allImages.length;
  
  allImages.forEach(img => {
    img.addEventListener('load', () => {
      measurements.loadedImages++;
      if (measurements.loadedImages === measurements.totalImages) {
        console.log(`✅ All ${measurements.totalImages} images loaded`);
      }
    });
  });

  return measurements;
};

/**
 * Initialize all performance monitoring
 */
export const initPerformanceMonitoring = () => {
  if (process.env.NODE_ENV === 'development') {
    console.log('🚀 Initializing image performance monitoring...');
    
    testBackgroundLoading();
    monitorImagePerformance();
    testLazyLoading();
    
    // Run comparison after page load
    window.addEventListener('load', () => {
      setTimeout(compareLoadingTimes, 100);
    });
  }
};
